/**
 * Which number a specification shows, and what that number means.
 *
 * Three different figures can be attached to an SDD, and they answer three different questions:
 *
 *   - **delivered** — an agent read the document and the repository and said how much of what the
 *     document describes is built. The question people actually ask.
 *   - **tasks** — how many of its tickets are done. A real measure of a plan, not of a document:
 *     a spec with two tickets both done is not a finished spec.
 *   - **document** — how complete an agent said the *writing* is. About the document, not the work.
 *
 * They used to be tried in that order and shown as one bare percentage, so a board reading "100%"
 * might mean finished, or might mean somebody finished writing the document. Every figure in this
 * product says how sure it is — cost says whether it is billed or estimated, progress is computed
 * from a plan's weights rather than claimed — and this one said nothing at all.
 *
 * So the fallback stays, because a spec with no estimate should still show something, but the kind
 * travels with the number and every screen is made to say which it is.
 */

import type { SpecSummary } from './types'

export type SpecProgressKind = 'delivered' | 'tasks' | 'document'

export interface SpecProgress {
  kind: SpecProgressKind
  percent: number
  /** True only for `delivered`: a model's reading, not a count of anything. */
  isEstimate: boolean
}

/**
 * The best figure a specification has, or null when it has none.
 *
 * Null is a real answer and screens must show it as one. A specification nobody has estimated and
 * that has no tickets is not a specification with nothing delivered.
 */
export function specProgress(spec: SpecSummary): SpecProgress | null {
  if (spec.estimatePercent != null) {
    return { kind: 'delivered', percent: clamp(spec.estimatePercent), isEstimate: true }
  }

  if (spec.taskCount > 0) {
    return { kind: 'tasks', percent: clamp((spec.tasksDone / spec.taskCount) * 100), isEstimate: false }
  }

  if (spec.documentCompleteness != null) {
    return { kind: 'document', percent: clamp(spec.documentCompleteness), isEstimate: false }
  }

  return null
}

function clamp(percent: number): number {
  if (!Number.isFinite(percent)) return 0
  return Math.min(100, Math.max(0, percent))
}
